import { Badge } from '@components/primitives/Badge.js'
import { formatCurrency, formatDate } from '@utils/format.js'
import { useDocumentPreviewUrl } from '@hooks/useDocumentPreviewUrl.js'
import type { DocItem } from './types.js'
import { formatBytes, getFileIcon, isImage, isPdf } from './utils.js'

type Props = {
  doc: DocItem
  onEdit: () => void
  onDelete: () => void
  onPreview: () => void
}

export function DocumentoCard({ doc, onEdit, onDelete, onPreview }: Props) {
  const previewUrl = useDocumentPreviewUrl(doc)
  const hasFile = Boolean(doc.hasArquivo)
  const nomeTitle = [doc.nome, doc.fornecedor, doc.categoria].filter(Boolean).join(' · ')
  const dataLabel = doc.data ? formatDate(doc.data) : '—'
  const sizeLabel = doc.tamanho != null && doc.tamanho > 0 ? formatBytes(doc.tamanho) : null
  const footMeta = sizeLabel ? `${dataLabel} · ${sizeLabel}` : dataLabel
  const isPhoto = Boolean(previewUrl && isImage(doc.tipo))

  return (
    <div className='cf3-doc-card' role='article' aria-label={`Documento: ${doc.nome}`}>
      <button
        className={`cf3-doc-card__thumb${isPhoto ? '' : ' cf3-doc-card__thumb--file'}`}
        onClick={onPreview}
        aria-label={`Visualizar ${doc.nome}`}
      >
        {isPhoto ? (
          <img
            src={previewUrl!}
            alt={doc.nome}
            className='cf3-doc-card__thumb-img'
            loading='lazy'
          />
        ) : (
          <span className='cf3-doc-card__file-icon' aria-hidden>
            {hasFile && isPdf(doc.tipo) ? 'PDF' : getFileIcon(doc.tipo)}
          </span>
        )}
      </button>
      <div className='cf3-doc-card__body cf3-doc-card__body--stack'>
        <p className='cf3-doc-card__nome' title={nomeTitle}>
          {doc.nome}
        </p>

        <div className='cf3-doc-card__line cf3-doc-card__line--tags'>
          <span className='cf3-doc-card__badge-wrap' title={doc.categoria}>
            <Badge label={doc.categoria} size='sm' />
          </span>
          <p className='cf3-doc-card__valor'>{formatCurrency(doc.valor ?? 0)}</p>
        </div>

        <div className='cf3-doc-card__line cf3-doc-card__line--foot'>
          <span className='cf3-doc-card__meta' title={footMeta}>
            {footMeta}
          </span>
          <div className='cf3-doc-card__actions'>
            <button
              className='cf3-doc-card__btn'
              onClick={onEdit}
              aria-label={`Editar ${doc.nome}`}
            >
              Editar
            </button>
            <button
              className='cf3-doc-card__btn cf3-doc-card__btn--danger'
              onClick={onDelete}
              aria-label={`Excluir ${doc.nome}`}
            >
              Excluir
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
